import {
    Box,
    Button,
    Card,
    Container,
    Dialog,
    Grid,
    Paper,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
    Zoom,
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import clsx from 'clsx';
import { Link, Spinner } from 'components';
import { ApplyForm } from 'components/jobseeker';
import { defaultCompanyLogo } from 'config';
import { useNotifications } from 'context/NotificationsContext';
import {
    ApplicationInviteStatus,
    Job,
    useAcceptApplicationInviteMutation,
    useJobseekerProfileQuery,
    useRejectApplicationInviteMutation,
} from 'graphql/generated';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getFilteredRequirements } from 'ui-services/jobseeker.ui-service';
import { useStyle } from './job-header.style';
import Brightness1Icon from '@material-ui/icons/Brightness1';

export const JobHeader: React.FC<{ job: Job & { salaryText?: string | null }; refetch: () => void }> = ({
    job,
    refetch,
}) => {
    const { salaryText, title, company, category, invitation, applicantProfileRequirements, acceptIncompleteResumes } =
        job;
    const [dialogOpen, setDialogOpen] = useState(false);
    const [requirementAlert, setRequirementAlert] = useState(false);
    const { notify, confirm } = useNotifications();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
    const closeDialog = (): void => setDialogOpen(false);
    const classes = useStyle();
    const { t } = useTranslation();
    const [rejectInvite] = useRejectApplicationInviteMutation({
        onCompleted() {
            notify({
                type: 'success',
                message: t('jobSeeker:rejectedSuccessfully'),
            });
            refetch();
        },
    });

    const [acceptInvite] = useAcceptApplicationInviteMutation({
        onCompleted() {
            notify({
                type: 'success',
                message: t('jobDetails:applySuccessful'),
            });
            closeDialog();
            refetch();
        },
    });
    const handleAccept = (formData) => {
        const { resumeId, coverLetter } = formData;
        if (invitation) {
            acceptInvite({ variables: { input: { applicationInviteId: invitation.id, coverLetter, resumeId } } });
        }
    };
    const { data: jobseekerProfile, loading } = useJobseekerProfileQuery({
        fetchPolicy: 'network-only',
    });
    const handleReject = () => {
        if (invitation) {
            confirm({
                onConfirm: () => rejectInvite({ variables: { input: { applicationInviteId: invitation.id } } }),
                description: t('jobSeeker:rejectDescription'),
            });
        }
    };
    const requirements = applicantProfileRequirements || [];
    const resumes = jobseekerProfile?.me?.jobseeker?.resumes.edges.map(({ node }) => node) || [];
    useEffect(() => {
        if (requirements && jobseekerProfile) {
            const reqs = getFilteredRequirements(requirements, jobseekerProfile);
            if (acceptIncompleteResumes) {
                setRequirementAlert(false);
            } else {
                if (reqs && reqs?.length > 0) {
                    setRequirementAlert(true);
                } else {
                    setRequirementAlert(false);
                }
            }
        }
    }, [jobseekerProfile]);
    return (
        <div className={classes.root}>
            <Container maxWidth="lg">
                <div className={classes.content}>
                    <div className={classes.leftSide}>
                        <Card className={clsx(classes.logo, !company.logo?.url && classes.defaultLogo)}>
                            <Link to={company.slug ? `/${company.slug}` : `/company/${company?.id}`}>
                                <img
                                    src={company.logo?.url || defaultCompanyLogo}
                                    onError={(e) => {
                                        if (e.currentTarget.src !== defaultCompanyLogo) {
                                            e.currentTarget.src = defaultCompanyLogo;
                                        }
                                    }}
                                    alt="company logo"
                                />
                            </Link>
                        </Card>
                        <div className={classes.detail}>
                            <Typography gutterBottom variant="h3">
                                {title}
                            </Typography>
                            {category && <p>{t(`category:${category.name}`)}</p>}
                            <Box display="flex" alignItems="center">
                                <Link link to={company.slug ? `/${company.slug}` : `/company/${company?.id}`}>
                                    <Typography variant="h5" color="textSecondary" className={classes.companyName}>
                                        {company.name}
                                    </Typography>
                                </Link>
                                {company.verified && (
                                    <Tooltip
                                        TransitionComponent={Zoom}
                                        placement="top"
                                        title={t('jobDetails:verifiedEmployer') || ''}
                                    >
                                        <CheckCircleIcon className={classes.verified} />
                                    </Tooltip>
                                )}
                            </Box>
                        </div>
                    </div>
                    {/* TODO//Baxış sayı 300-400 olanda açarıq commentdən)) */}
                    {/* <Box className={classes.viewCount}>
                        <Typography variant="h6" color="textSecondary">
                            Baxış sayı: {viewCount}
                        </Typography>
                    </Box> */}

                    {salaryText && (
                        <Card className={classes.salaryBox}>
                            <div className={classes.salaryType}>{t('jobDetails:monthlySalary')}</div>
                            <div className={classes.salaryAmount}>{salaryText}</div>
                        </Card>
                    )}
                </div>

                <Grid item xs={12} container justify="flex-end" spacing={2} className={classes.inviteButtonsContainer}>
                    {invitation?.status === ApplicationInviteStatus.Pending ? (
                        <>
                            <Grid item>
                                <Button
                                    onClick={() => {
                                        setDialogOpen(true);
                                    }}
                                    variant="contained"
                                    color="primary"
                                >
                                    {t(`jobSeeker:accept`)}
                                </Button>
                            </Grid>
                            <Grid item>
                                <Button
                                    onClick={() => {
                                        handleReject();
                                    }}
                                    variant="outlined"
                                    color="primary"
                                >
                                    {t(`jobSeeker:rejectOffer`)}
                                </Button>
                            </Grid>
                        </>
                    ) : invitation?.status === ApplicationInviteStatus.Rejected ? (
                        <Paper className={classes.statusContent}>
                            <Brightness1Icon className={clsx(classes.inviteRejected, classes.rejectedIcon)} />
                            <Typography noWrap variant="body2" className={classes.status}>
                                {t(`jobSeeker:rejected`)}
                            </Typography>
                        </Paper>
                    ) : null}
                </Grid>
            </Container>
            <Dialog classes={{ paper: classes.dialog }} open={dialogOpen} fullScreen={isMobile} onClose={closeDialog}>
                {loading ? (
                    <Box className={classes.spinner}>
                        <Spinner />
                    </Box>
                ) : (
                    <ApplyForm
                        closeDialog={closeDialog}
                        loading={loading}
                        onSubmit={handleAccept}
                        requirementAlert={requirementAlert}
                        requirements={requirements}
                        resumes={resumes}
                        title={t('jobDetails:applyToJob')}
                    />
                )}
            </Dialog>
        </div>
    );
};
